import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa6';

import type { HeaderPrimaryCtaChoice } from '@/common/header';
import { IntercomLauncher } from '@/common/intercom-launcher';
import LanguageSwitcher from '@/common/language-switcher';
import { BTN_PRIMARY_SM } from '@/constants/button-classes';
import { FOOTER_BLOG_HREFS, FOOTER_FEEDBACK_HREFS, FOOTER_GUIDE_HREFS, FOOTER_HELP_HREFS, FOOTER_NOPING_HREFS, FOOTER_SOCIALS, FOOTER_TECH_HREFS } from '@/constants/footer-data';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/constants/store-links';
import { CountryCodeEnum } from '@/enums/country-code-enum';
import { getBlogUrl } from '@/utils/get-blog-url';
import { getUserCountryCode } from '@/utils/get-user-country-code';
import { HOME_CTA_ASSINE_HREF, type HomeCtaSlot, homeCtaSlot } from '@/utils/home-cta-cookie';

interface SocialCssVars extends CSSProperties {
  '--brand': string;
}

const HEADING_CLASS = 'font-sans text-sm font-bold uppercase tracking-[0.05em] text-[var(--np-fg)]';
const LIST_CLASS = 'mt-[18px] flex list-none flex-col gap-2.5 p-0 pb-4 lg:pb-0';
const LINK_CLASS = 'font-sans text-[13px] font-medium leading-[1.4] text-[var(--np-fg-dim)] no-underline transition-colors duration-150 hover:text-[var(--np-green)]';
const SOCIAL_LINK_CLASS = 'inline-flex size-9 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] text-[var(--np-fg-dim)] no-underline transition-[color,background,border-color] duration-200 hover:border-[var(--brand)] hover:bg-white/[0.08] hover:text-[var(--brand)]';
const STORE_LINK_CLASS = 'inline-flex h-[75px] items-center overflow-hidden rounded-lg max-[640px]:h-11';
const SUBTITLE_CLASS = 'text-sm leading-[1.7] text-[var(--np-fg-dim)]';

interface FooterLinkColumnProps {
  id: string;
  title: string;
  titleHref: string;
  titleLocaleFalse?: boolean;
  labels: string[];
  hrefs: string[];
  /** Parallel to `hrefs`: the group (src/utils/home-cta-cookie.ts) an entry is shown to, when it is not shown to everyone. */
  slots?: (HomeCtaSlot | undefined)[];
  isDesktop: boolean;
  children?: ReactNode;
}

function FooterLinkColumn({ id, title, titleHref, titleLocaleFalse, labels, hrefs, slots, isDesktop, children }: FooterLinkColumnProps) {
  const [open, setOpen] = useState(false);
  const panelId = `footer-col-${id}`;
  const headingId = `footer-col-${id}-heading`;
  return (
    <div className='min-w-0 border-b border-white/[0.06] lg:border-0'>
      <h4 id={headingId} className='m-0'>
        {isDesktop ? (
          <Link href={titleHref} locale={titleLocaleFalse ? false : undefined} prefetch={false} className={`flex w-full items-center gap-3 lg:p-0 ${HEADING_CLASS} no-underline transition-colors duration-150 hover:text-[var(--np-green)]`}>
            <span>{title}</span>
          </Link>
        ) : (
          <button type='button' aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((current) => !current)} className={`flex w-full cursor-pointer items-center justify-between gap-3 py-4 text-start ${HEADING_CLASS}`}>
            <span>{title}</span>
            <FaPlus aria-hidden='true' size={12} className={`text-[var(--np-fg-dim)] transition-transform duration-200 ${open ? 'rotate-45' : ''}`} />
          </button>
        )}
      </h4>
      <div id={panelId} role='region' aria-labelledby={headingId} className={`${open ? 'block' : 'hidden'} lg:block`}>
        <ul className={LIST_CLASS}>
          {hrefs.map((href, index) => {
            const slot = slots?.[index];
            return (
              <li key={`${id}-${index}`} {...(slot ? homeCtaSlot(slot) : {})}>
                {/* prefetch=false: these repeat the header destinations, and nothing in the footer needs a
                  viewport prefetch — by the time it is on screen, hover covers the navigation. */}
                <Link href={href} prefetch={false} className={LINK_CLASS}>
                  {labels[index] ?? ''}
                </Link>
              </li>
            );
          })}
        </ul>
        {children}
      </div>
    </div>
  );
}

function asArray(value: unknown): string[] {
  return Array.isArray(value) ? (value as string[]) : [];
}

const useIsDesktop = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsDesktop(mq.matches);
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
};

/** The free-trial entry of the NOPING column, dropped on pages that offer no trial. */
const TRIAL_HREF = '/trial';

/**
 * `primaryCta`: CTA no lugar do teste grátis, como o do header (o "Assine agora" das versões assine da Home). A página que
 * troca o CTA não oferece teste, então o footer também tira as outras menções a ele: a frase do teste no texto da marca e o
 * link "Teste 1 Dia Grátis" da coluna NOPING (QA do SCRUM-2487). Os três estados são os do header (`HeaderPrimaryCtaChoice`):
 * ausente, o footer segue o cookie da versão sorteada na Home e sai com as duas formas, cada uma marcada para o CSS inline
 * do `_document` mostrar só a do grupo do visitante (SCRUM-2631).
 */
export default function Footer({ primaryCta }: { primaryCta?: HeaderPrimaryCtaChoice } = {}) {
  const { t, i18n } = useTranslation('common');
  const [country, setCountry] = useState<CountryCodeEnum>(CountryCodeEnum.UNITED_STATES);
  const isDesktop = useIsDesktop();
  const followsCookie = primaryCta === undefined;

  useEffect(() => {
    setCountry(getUserCountryCode());
  }, []);

  const isBrazil = country === CountryCodeEnum.BRAZIL;
  const isRussia = country === CountryCodeEnum.RUSSIAN;
  const isChinaOrHongKong = country === CountryCodeEnum.CHINESE || country === CountryCodeEnum.HONG_KONG;
  const isPt = (i18n.language || 'en').startsWith('pt');

  const appStoreSrc = isPt ? '/assets/images/footer/download-app-store-pt.webp' : '/assets/images/footer/download-app-store.webp';
  const googlePlaySrc = isPt ? '/assets/images/footer/download-google-play-pt.webp' : '/assets/images/footer/download-google-play.webp';

  const columns = [
    { id: 'noping', titleKey: 'footer.cols.noping', titleHref: '/', labelsKey: 'footer.links_noping', hrefs: FOOTER_NOPING_HREFS },
    { id: 'tech', titleKey: 'footer.cols.tech', titleHref: '/technology', labelsKey: 'footer.links_tech', hrefs: FOOTER_TECH_HREFS },
    { id: 'help', titleKey: 'footer.cols.help', titleHref: '/support', labelsKey: 'footer.links_help', hrefs: FOOTER_HELP_HREFS },
    { id: 'blog', titleKey: 'footer.cols.blog', titleHref: getBlogUrl(i18n.language), titleLocaleFalse: true, labelsKey: 'footer.links_blog', hrefs: FOOTER_BLOG_HREFS },
    { id: 'guide', titleKey: 'footer.cols.guide', titleHref: '/technology', labelsKey: 'footer.links_guide', hrefs: FOOTER_GUIDE_HREFS },
    { id: 'feedback', titleKey: 'footer.cols.feedback', titleHref: '/evaluate-our-platform', labelsKey: 'footer.links_feedback', hrefs: FOOTER_FEEDBACK_HREFS },
  ];

  return (
    <footer className='border-t border-white/10 bg-black'>
      <IntercomLauncher />
      <div className='mx-auto w-full max-w-[1400px] px-5 pb-20 pt-5'>
        <div className='flex flex-col justify-between gap-5 border-b border-white/10 pb-5 lg:flex-row'>
          <div className='flex flex-wrap items-center gap-5 max-[640px]:justify-center max-[640px]:gap-3'>
            <span className='font-sans text-[13px] text-[var(--np-fg-dim)] max-[640px]:basis-full max-[640px]:text-center'>{t('footer.strategic_partnership')}</span>
            <Image src='/assets/images/footer/microsoft.webp' alt='Microsoft Certified' width={97} height={68} className='h-auto max-[640px]:h-9 max-[640px]:w-auto' />
            <Image src='/assets/images/footer/digicert.webp' alt='DigiCert Secured' width={149} height={68} className='h-auto max-[640px]:h-9 max-[640px]:w-auto' />
            <Image src='/assets/images/footer/google-cloud.webp' alt='Google Cloud' width={180} height={68} className='h-auto max-[640px]:h-9 max-[640px]:w-auto' />
          </div>
          <div className='flex flex-wrap items-center gap-5 max-[640px]:justify-center'>
            <Link href={APP_STORE_URL} target='_blank' rel='noopener noreferrer' aria-label={t('footer.aria_app_store')} className={STORE_LINK_CLASS}>
              <Image src={appStoreSrc} alt={t('footer.aria_app_store')} width={178} height={52} className='h-auto max-[640px]:h-11 max-[640px]:w-auto' />
            </Link>
            <Link href={PLAY_STORE_URL} target='_blank' rel='noopener noreferrer' aria-label={t('footer.aria_google_play')} className={STORE_LINK_CLASS}>
              <Image src={googlePlaySrc} alt={t('footer.aria_google_play')} width={170} height={52} className='h-auto max-[640px]:h-11 max-[640px]:w-auto' />
            </Link>
          </div>
        </div>

        <div className='grid min-w-0 items-center gap-5 py-5 lg:grid-cols-[1.4fr_1fr]'>
          <div className='flex min-w-0 flex-col gap-5 max-[640px]:text-start'>
            <h3 className='text-3xl font-bold leading-tight text-[var(--np-fg)] max-[640px]:text-[clamp(20px,5vw,24px)]'>{t('footer.brand_title')}</h3>
            {followsCookie ? (
              <>
                <p className={SUBTITLE_CLASS} {...homeCtaSlot('teste')}>
                  {t('footer.brand_subtitle')}
                </p>
                <p className={SUBTITLE_CLASS} {...homeCtaSlot('assine')}>
                  {t('footer.brand_subtitle_no_trial')}
                </p>
              </>
            ) : (
              <p className={SUBTITLE_CLASS}>{t(primaryCta ? 'footer.brand_subtitle_no_trial' : 'footer.brand_subtitle')}</p>
            )}
          </div>

          <div className='flex min-w-0 flex-col items-start gap-5 lg:items-end'>
            {/* prefetch=false: this link is rendered on every page via the footer, so once scrolled
                into view a prefetch would pull the whole /trial bundle (the trial LP registry)
                regardless of user intent */}
            {followsCookie ? (
              <>
                <Link href={TRIAL_HREF} prefetch={false} className={BTN_PRIMARY_SM} {...homeCtaSlot('teste')}>
                  {t('footer.cta')}
                </Link>
                <Link href={HOME_CTA_ASSINE_HREF} prefetch={false} className={BTN_PRIMARY_SM} {...homeCtaSlot('assine')}>
                  {t('header.button_subscribe_now')}
                </Link>
              </>
            ) : (
              <Link href={primaryCta?.href ?? TRIAL_HREF} prefetch={false} className={BTN_PRIMARY_SM}>
                {primaryCta?.label ?? t('footer.cta')}
              </Link>
            )}
            <div className='flex max-w-full flex-wrap items-center gap-2 rounded-[10px] border border-[rgba(156,255,46,0.18)] bg-[rgba(156,255,46,0.05)] px-3.5 py-2.5 text-xs leading-[1.3] text-[var(--np-fg-dim)]'>
              <span aria-hidden='true' className='text-[13px] tracking-[0.5px] text-[#ffb547] [text-shadow:0_0_6px_rgba(255,181,71,0.3)]'>
                ★★★★★
              </span>
              <span className='inline-flex flex-wrap items-center gap-1'>
                <strong>{t('footer.rating_value')}</strong> {t('footer.rating_reviews')}
                <span aria-hidden='true' className='mx-0.5 text-[var(--np-fg-faint)]'>
                  ·
                </span>
                <span>{t('footer.players_short')}</span>
                <span aria-hidden='true' className='mx-0.5 text-[var(--np-fg-faint)]'>
                  ·
                </span>
                <span>{t('footer.market_years')}</span>
              </span>
            </div>
          </div>
        </div>

        <nav aria-label={t('footer.cols.noping')} className='grid min-w-0 grid-cols-1 gap-0 border-t border-white/[0.06] py-5 lg:grid-cols-6 lg:gap-8'>
          {columns.map((column) => {
            // Labels and hrefs are parallel lists, so the trial entry leaves both at the same index.
            const allLabels = asArray(t(column.labelsKey, { returnObjects: true }));
            // Seguindo o cookie, o link do teste fica no HTML e o CSS inline o esconde no grupo "Assine agora".
            const links = column.hrefs.map((href, index) => ({ href, label: allLabels[index] ?? '' })).filter((link) => !(primaryCta && link.href === TRIAL_HREF));
            const labels = links.map((link) => link.label);
            const hrefs = links.map((link) => link.href);
            const slots = links.map((link) => (followsCookie && link.href === TRIAL_HREF ? 'teste' : undefined));
            const extras =
              column.id === 'feedback' ? (
                <div className='mt-5 pb-4 lg:pb-0'>
                  <LanguageSwitcher />
                </div>
              ) : undefined;
            return (
              <FooterLinkColumn key={column.id} id={column.id} title={t(column.titleKey)} titleHref={column.titleHref} titleLocaleFalse={column.titleLocaleFalse} labels={labels} hrefs={hrefs} slots={slots} isDesktop={isDesktop}>
                {extras}
              </FooterLinkColumn>
            );
          })}
        </nav>

        <div className='mt-5 flex flex-col items-start justify-between gap-5 border-t border-white/[0.08] pt-5 max-[900px]:items-start md:flex-row md:items-center'>
          <nav aria-label={t('footer.aria_social')} className='flex flex-wrap items-center gap-2.5'>
            {FOOTER_SOCIALS.map((social) => {
              const style: SocialCssVars = { '--brand': social.color };
              return (
                <Link key={social.name} href={social.href} target='_blank' rel='noopener noreferrer' aria-label={social.name} style={style} className={SOCIAL_LINK_CLASS}>
                  <social.Icon size={16} aria-hidden />
                </Link>
              );
            })}
          </nav>
          <address className='flex min-h-[42px] flex-col gap-1 font-mono text-[11px] not-italic tracking-widest text-[var(--np-fg-mute)] max-[900px]:min-h-0 md:items-end md:text-end'>
            <span className='leading-tight'>{isBrazil ? t('footer.footer_copyright') : t('footer.footer_copyright_alt')}</span>
            {isBrazil && (
              <>
                <span className='leading-tight'>{t('footer.br_cnpj')}</span>
                <span className='leading-tight'>{t('footer.br_address')}</span>
              </>
            )}
            {isRussia && (
              <>
                <span className='leading-tight'>{t('footer.footer_ru_1')}</span>
                <span className='leading-tight'>{t('footer.footer_ru_2')}</span>
                <span className='leading-tight'>{t('footer.footer_ru_3')}</span>
              </>
            )}
            {isChinaOrHongKong && (
              <>
                <span className='leading-tight'>{t('footer.footer_ch_1')}</span>
                <span className='leading-tight'>{t('footer.footer_ch_2')}</span>
              </>
            )}
          </address>
        </div>
      </div>
    </footer>
  );
}
